import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/combobox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useA11ySettings } from "@/lib/hooks/useA11ySettings";
import useAccessibilitySettings from "@/lib/hooks/useAccessibilitySettings";
import { useLocalStorage } from "@/lib/hooks/useLocalStorage";
import { A11ySettings } from "@/lib/contexts/A11yProvider";
import useUserSettings from "@/lib/queries/user/useUserSettings";
import {
  LANGUAGES,
  DEFAULT_DISPLAY_LANGUAGE,
  DISPLAY_LANGUAGE_CODES,
} from "@/lib/constants/languages";
import {
  getDisplayLanguageOptions,
  getResultLanguageOptionsWithSame,
} from "@/lib/utils/languageOptions";
import { getBrowserPreferredLanguage } from "@/lib/utils/browserLanguage";
import { useStytchSession } from "@stytch/react";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Check, FileDashed, Gear, Globe } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import LanguageSettingsButton from "./LanguageSettingsButton";

// Extended settings interface to include display and result languages
interface ExtendedA11ySettings extends A11ySettings {
  displayLanguage?: string;
  resultLanguage?: string | null;
}

interface AccessibilitySettingsPopupProps {
  className?: string;
}

// Helper function to convert display language to A11y language code
const getA11yLanguageFromDisplay = (displayLanguage: string): string => {
  return (
    DISPLAY_LANGUAGE_CODES[
      displayLanguage as keyof typeof DISPLAY_LANGUAGE_CODES
    ] || "en"
  );
};

const getDisplayLanguageFromA11y = (a11yLanguage: string): string => {
  // Find the display language key that maps to this A11y language code
  const displayLanguageKey = Object.keys(DISPLAY_LANGUAGE_CODES).find(
    (key) =>
      DISPLAY_LANGUAGE_CODES[key as keyof typeof DISPLAY_LANGUAGE_CODES] ===
      a11yLanguage
  );
  return displayLanguageKey || DEFAULT_DISPLAY_LANGUAGE;
};

function AccessibilitySettingsPopup({
  className,
}: AccessibilitySettingsPopupProps) {
  const { t } = useTranslation(["common", "settings"]);
  const { session, fromCache } = useStytchSession();

  const { settings: currentSettings, updateSettings } = useA11ySettings();
  const { useUpdateAccessibilitySettings } = useAccessibilitySettings();
  const { useGetUserSettings: useGetUser } = useUserSettings();
  const updateAccessibilityMutation = useUpdateAccessibilitySettings();
  const isLoggedIn = !!session && !fromCache;

  const { data: userSettings } = useGetUser({
    enabled: isLoggedIn,
  });

  const [localStorageSettings, setLocalStorageSettings] =
    useLocalStorage<ExtendedA11ySettings>("a11y-settings", {
      darkMode: false,
      highContrast: false,
      lowAnimation: false,
      dyslexiaFont: false,
      largeText: false,
      language: getBrowserPreferredLanguage(), // Use browser preferred language as fallback
      displayLanguage: DEFAULT_DISPLAY_LANGUAGE,
      resultLanguage: null,
    });

  const [tempSettings, setTempSettings] = useState<ExtendedA11ySettings>(() => {
    if (isLoggedIn) {
      // For logged-in users, use user settings
      const displayLang =
        userSettings?.displayLanguage ?? DEFAULT_DISPLAY_LANGUAGE;
      return {
        ...currentSettings,
        displayLanguage: displayLang,
        resultLanguage: userSettings?.resultLanguage ?? null,
        // Ensure A11y language is synced with display language
        language: getA11yLanguageFromDisplay(displayLang),
      };
    } else {
      // For non-logged-in users, use localStorage settings and derive displayLanguage from A11y language
      const displayLang = getDisplayLanguageFromA11y(
        localStorageSettings.language || getBrowserPreferredLanguage()
      );
      return {
        ...currentSettings,
        ...localStorageSettings,
        displayLanguage: displayLang,
        resultLanguage: localStorageSettings.resultLanguage ?? null,
      };
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (isOpen) {
      let initialSettings: ExtendedA11ySettings;

      if (isLoggedIn) {
        const displayLang =
          userSettings?.displayLanguage ?? DEFAULT_DISPLAY_LANGUAGE;
        initialSettings = {
          ...currentSettings,
          displayLanguage: displayLang,
          resultLanguage: userSettings?.resultLanguage ?? null,
          // Ensure A11y language is synced with display language
          language: getA11yLanguageFromDisplay(displayLang),
        };
      } else {
        // For non-logged users, derive display language from A11y language
        const displayLang = getDisplayLanguageFromA11y(
          localStorageSettings.language || getBrowserPreferredLanguage()
        );
        const syncedSettings = {
          ...localStorageSettings,
          displayLanguage: displayLang,
          language:
            localStorageSettings.language || getBrowserPreferredLanguage(),
        };
        initialSettings = syncedSettings;
      }

      setTempSettings(initialSettings);
      setHasUnsavedChanges(false);
    }
  }, [isOpen, currentSettings, localStorageSettings, isLoggedIn, userSettings]);

  useEffect(() => {
    let currentExtended: ExtendedA11ySettings;

    if (isLoggedIn) {
      currentExtended = {
        ...currentSettings,
        displayLanguage: userSettings?.displayLanguage ?? LANGUAGES.ENGLISH,
        resultLanguage: userSettings?.resultLanguage ?? null,
      };
    } else {
      // For non-logged users, use localStorage settings
      const displayLang = getDisplayLanguageFromA11y(
        currentSettings.language || getBrowserPreferredLanguage()
      );
      currentExtended = {
        ...currentSettings,
        displayLanguage: displayLang,
        resultLanguage: localStorageSettings.resultLanguage ?? null,
      };
    }

    const hasChanges =
      JSON.stringify(tempSettings) !== JSON.stringify(currentExtended);
    setHasUnsavedChanges(hasChanges);
  }, [
    tempSettings,
    currentSettings,
    userSettings,
    isLoggedIn,
    localStorageSettings,
  ]);

  const handleSettingChange = (
    key: keyof ExtendedA11ySettings,
    value: boolean | string | null
  ) => {
    setTempSettings((prev) => {
      const newSettings = {
        ...prev,
        [key]: value,
      };

      // If displayLanguage is being changed, also update the language property for A11y
      if (key === "displayLanguage" && typeof value === "string") {
        const a11yLanguageCode = getA11yLanguageFromDisplay(value);
        newSettings.language = a11yLanguageCode;
      }

      return newSettings;
    });
  };

  const { useUpdateUser } = useUserSettings();
  const updateUserMutation = useUpdateUser();

  const handleSave = async () => {
    if (isLoggedIn) {
      // Save to user account via API
      try {
        // Save accessibility settings (preferences + language for a11y)
        const a11ySettings: A11ySettings = {
          darkMode: tempSettings.darkMode,
          highContrast: tempSettings.highContrast,
          lowAnimation: tempSettings.lowAnimation,
          dyslexiaFont: tempSettings.dyslexiaFont,
          largeText: tempSettings.largeText,
          language: tempSettings.language,
        };
        await updateAccessibilityMutation.mutateAsync(a11ySettings);

        if (
          userSettings &&
          (tempSettings.displayLanguage !== userSettings.displayLanguage ||
            tempSettings.resultLanguage !== userSettings.resultLanguage)
        ) {
          const updatedUserSettings = {
            ...userSettings,
            displayLanguage: tempSettings.displayLanguage,
            resultLanguage: tempSettings.resultLanguage,
          };
          await updateUserMutation.mutateAsync(updatedUserSettings);
        }

        updateSettings(a11ySettings);
        setIsOpen(false);
      } catch (error) {
        console.error("Failed to save accessibility settings:", error);
      }
    } else {
      setLocalStorageSettings(tempSettings);
      const a11ySettings: A11ySettings = {
        darkMode: tempSettings.darkMode,
        highContrast: tempSettings.highContrast,
        lowAnimation: tempSettings.lowAnimation,
        dyslexiaFont: tempSettings.dyslexiaFont,
        largeText: tempSettings.largeText,
        language: tempSettings.language,
      };
      updateSettings(a11ySettings);
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    setTempSettings(currentSettings);
    setHasUnsavedChanges(false);
    setIsOpen(false);
  };

  const handleReset = () => {
    const defaultDisplayLanguage = DEFAULT_DISPLAY_LANGUAGE;
    const defaultSettings: ExtendedA11ySettings = {
      darkMode: false,
      highContrast: false,
      lowAnimation: false,
      dyslexiaFont: false,
      largeText: false,
      language: getA11yLanguageFromDisplay(defaultDisplayLanguage), // Ensure sync
      displayLanguage: defaultDisplayLanguage,
      resultLanguage: null,
    };
    setTempSettings(defaultSettings);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <div className={className}>
          <LanguageSettingsButton />
        </div>
      </DialogTrigger>
      <DialogContent className="w-[95vw] max-w-md mx-auto max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Gear className="w-4 h-4 sm:w-5 sm:h-5" />
            {t("settings:accessibility.title", "Accessibility Settings")}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            {isLoggedIn
              ? t(
                  "settings:accessibility.description.loggedIn",
                  "Customize your accessibility preferences. These settings will be saved to your account."
                )
              : t(
                  "settings:accessibility.description.guest",
                  "Customize your accessibility preferences. These settings will be saved locally on this device."
                )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-3 sm:gap-4 sm:py-4">
          {/* Language Selection */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="text-xs font-medium sm:text-sm">
              {t("settings:language.title", "Language Settings")}
            </h4>

            {/* Display Language */}
            <div className="space-y-1.5 sm:space-y-2">
              <Label
                htmlFor="displayLanguage"
                className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"
              >
                <Globe className="w-3 h-3 sm:w-4 sm:h-4" />{" "}
                {t("settings:displayLanguage")}
              </Label>
              <SearchableSelect
                options={getDisplayLanguageOptions()}
                value={tempSettings.displayLanguage || DEFAULT_DISPLAY_LANGUAGE}
                onValueChange={(value) =>
                  handleSettingChange("displayLanguage", value)
                }
                placeholder={t(
                  "settings:selectLanguage",
                  "Select a language..."
                )}
                searchPlaceholder={t(
                  "settings:searchLanguage",
                  "Search languages..."
                )}
                emptyText={t("settings:noLanguageFound", "No language found.")}
              />
              <p className="text-[10px] text-muted-foreground sm:text-xs">
                {t("settings:displayLanguageDescription")}
              </p>
            </div>

            <div className="space-y-1.5 sm:space-y-2">
              <Label
                htmlFor="resultLanguage"
                className="flex items-center gap-1.5 text-xs sm:text-sm sm:gap-2"
              >
                <FileDashed className="w-3 h-3 sm:w-4 sm:h-4" />
                {t("settings:resultLanguage")}
              </Label>
              <SearchableSelect
                options={getResultLanguageOptionsWithSame(
                  t("settings:sameAsDisplay", "Same as display language")
                )}
                value={tempSettings.resultLanguage || "same"}
                onValueChange={(value) =>
                  handleSettingChange(
                    "resultLanguage",
                    value === "same" ? null : value
                  )
                }
                placeholder={t(
                  "settings:language.result.placeholder",
                  "Select content language..."
                )}
                searchPlaceholder={t(
                  "settings:searchLanguage",
                  "Search languages..."
                )}
                emptyText={t("settings:noLanguageFound", "No language found.")}
              />
              <p className="text-[10px] text-muted-foreground sm:text-xs">
                {t("settings:resultLanguageDescription")}
              </p>
            </div>
          </div>

          <Separator />

          {/* Visual Settings */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="text-xs font-medium sm:text-sm">
              {t("settings:visual", "Visual")}
            </h4>

            <div className="flex items-center justify-between">
              <Label
                htmlFor="dark-mode"
                className="text-xs font-normal sm:text-sm"
              >
                {t("settings:darkMode", "Dark Mode")}
              </Label>
              <Switch
                id="dark-mode"
                checked={tempSettings.darkMode}
                onCheckedChange={(checked) =>
                  handleSettingChange("darkMode", checked)
                }
                className="scale-75 sm:scale-100"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label
                htmlFor="high-contrast"
                className="text-xs font-normal sm:text-sm"
              >
                {t("settings:highContrast", "High Contrast")}
              </Label>
              <Switch
                id="high-contrast"
                checked={tempSettings.highContrast}
                onCheckedChange={(checked) =>
                  handleSettingChange("highContrast", checked)
                }
                className="scale-75 sm:scale-100"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label
                htmlFor="large-text"
                className="text-xs font-normal sm:text-sm"
              >
                {t("settings:largeText", "Large Text")}
              </Label>
              <Switch
                id="large-text"
                checked={tempSettings.largeText}
                onCheckedChange={(checked) =>
                  handleSettingChange("largeText", checked)
                }
                className="scale-75 sm:scale-100"
              />
            </div>
          </div>

          <Separator />

          {/* Motion & Reading Settings */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="text-xs font-medium sm:text-sm">
              {t("settings:motionReading", "Motion & Reading")}
            </h4>

            <div className="flex items-center justify-between">
              <Label
                htmlFor="low-animation"
                className="text-xs font-normal sm:text-sm"
              >
                {t("settings:reduceMotion", "Reduce Motion")}
              </Label>
              <Switch
                id="low-animation"
                checked={tempSettings.lowAnimation}
                onCheckedChange={(checked) =>
                  handleSettingChange("lowAnimation", checked)
                }
                className="scale-75 sm:scale-100"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label
                htmlFor="dyslexia-font"
                className="text-xs font-normal sm:text-sm"
              >
                {t("settings:dyslexiaFont", "Dyslexia-Friendly Font")}
              </Label>
              <Switch
                id="dyslexia-font"
                checked={tempSettings.dyslexiaFont}
                onCheckedChange={(checked) =>
                  handleSettingChange("dyslexiaFont", checked)
                }
                className="scale-75 sm:scale-100"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-1.5 sm:gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            size="sm"
            className="px-2 text-xs sm:text-sm sm:px-3"
          >
            {t("reset", "Reset")}
          </Button>
          <div className="flex-1" />
          <Button
            variant="ghost"
            onClick={handleCancel}
            size="sm"
            className="px-2 text-xs sm:text-sm sm:px-3"
          >
            {t("cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleSave}
            size="sm"
            disabled={
              !hasUnsavedChanges || updateAccessibilityMutation.isPending
            }
            className={cn(
              "text-xs sm:text-sm px-2 sm:px-3",
              hasUnsavedChanges &&
                !updateAccessibilityMutation.isPending &&
                "animate-bounce-once"
            )}
          >
            <Check className={cn("w-3 h-3 mr-1 sm:w-4 sm:h-4")} />
            {updateAccessibilityMutation.isPending
              ? t("common:saving", "Saving...")
              : t("save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AccessibilitySettingsPopup;
